<template>
    <div data-name="ResponsibilityAttachedRolesTab">
        <OrganizationRolesTable
            ref="organizationRolesTableRef"
            :pagination="paginationComputed" 
            :useNewBadge="true" 
            :always-show-horizontal-scrollbar="true"
            :dataStream="rolesData" 
            :loading="skeletonLoading"
            :initialFilters="appliedFilters"
            :use-persisted-filters="false"
            :keep-explicit-archived-filter="true"
            :disable-attached-roles-actions="responsibilityArchived"
            :tooltip-for-disabled-attached-roles-actions="responsibilityArchived && showTooltipWhenAttachedRolesBlocked"
            :showAttachToPositionsBulk="false"
            :show_detach_button="true"
            :show-detach-button="true"
            :detachContextObject="detachContextObject"
            :detachRoleDialogParentKind="'responsibility'"
            :add_button_text="'Attach to Role'"
            :emit_add_action="true"
            :emit_role_drilldown="true"
            :allow-drag-role-to-tree="false"
            :enable-attached-roles-reorder="false"
            :chatterData="chatterData"
            :totalChatterData="totalChatterData"
            @onSearchChange="handleSearchChange"
            @onFilterChange="handleFilterChange"
            @onPageChange="handlePageChange"
            @onRowOperation="handleRowOperation"
            @onAddRoleClick="handleOpenAttachRoleDialog"
            @onRoleDrillDown="handleRoleDrillDown"
            @openChatterDrawer="handleOpenChatterDrawer"
            @detach="handleDetach"
            @detach-inline="handleDetachInline"
            @requestMoreCheckpointsChatter="handleRequestMoreCheckpointsChatter"
        />

        <AttachedResponsibilityToRole
            v-model="showAttachRoleDialog"
            OrgObjectType="Responsibility to Role"
            @onSave="attachSelectedRoleToResponsibility"
            :type="
                showAttachResponsibilitiesPicker
                    ? 'respToRole_new_picker'
                    : 'respToRole'
            "
            :items="attachableRoles"
            @item-selected="handleRoleSelected"
            @add-item-selected="handleAddNewRole"
            :parentName="responsibilityDataObject.name || ''"
            :existing-names-list="attachedRoleNames"
            :hasMoreData="hasMoreRolesData"
            :isLoadingMore="isLoadingMoreRoles"
            @load-more="loadMoreRoles"
            @search="searchRoles"
            :isAssigning="isAssigningRole"
        />

        <ActivateRoleDialog
            :value="showActivateRoleDialog"
            @input="showActivateRoleDialog = $event"
            @onSave="onActivateRoleConfirm"
            :name="currentRole?.role_name || currentRole?.data?.name || ''"
            :employee-count="activationEmployeeCount"
        />
        <ActivateResponsibilityConfirmationModal
            :dialog="showActivateResponsibility"
            @close="showActivateResponsibility = false"
            @skip="skipActivateResponsibility"
            @activate="activateAttachedResponsibilities"
            :role="currentRole?.role_name || currentRole?.data?.name || ''"
            :draftResponsibilityCount="draftResponsibilityCount"
            :skipButtonLoader="skipActivateButtonLoader"
            :activateButtonLoader="activateAttachedButtonLoader"
        />
        <RetireRoleDialog
            :show="showRetireRoleDialog"
            @onClose="showRetireRoleDialog = false"
            @onRetire="retireRole"
            :name="currentRole?.role_name || currentRole?.data?.name || ''"
            :role="currentRole"
        />
        <ReactivateRoleDialog
            :show="showReActivateRoleDialog"
            @onClose="showReActivateRoleDialog = false"
            @onReactivate="reActivateRole"
            :name="currentRole?.role_name || currentRole?.data?.name || ''"
            :role="currentRole"
            :employee-count="activationEmployeeCount"
        />
        <RenameRoleDialog
            v-if="showRenameRoleDialog && renameRoleModel"
            v-model="showRenameRoleDialog"
            :role="renameRoleModel"
            @saved="onRoleRenamed"
        />
        <DeleteRoleDialog
            :show="showDeleteRoleDialog"
            @onClose="showDeleteRoleDialog = false"
            @onDelete="deleteRole"
            :name="currentRole?.role_name || currentRole?.data?.name || ''"
        />
        <ArchivePopUp
            :action="archiveAction"
            type="role"
            :show="showArchiveDialog"
            :curretObj="currentRole"
            @onClose="showArchiveDialog = false"
            @onArchive="onArchiveRole"
        />
        <AttachedResponsibility
            v-model="showAttachResponsibilityDialog"
            @onSave="handleAttachResponsibilityToRole"
            :items="attachableResponsibilities"
            @item-selected="handleResponsibilitySelected"
            @add-item-selected="handleAddNewResponsibility"
            :parentName="currentRole?.role_name || currentRole?.data?.name || ''"
            :openDefault="openDefaultAttachResponsibilityDialog"
            @input="closeAttachResponsibilityDialog"
            :type="
                showAttachResponsibilitiesPicker
                    ? 'attach_responsibility_role_new_picker'
                    : 'attach_responsibility_role'
            "
            @load-more="loadMoreResponsibilities"
            @search="searchResponsibilities"
            :hasMoreData="hasMoreResponsibilitiesData"
            :isLoadingMore="isLoadingMoreResponsibilities"
            :isAssigning="isAssigningResponsibility"
            :attach-target="attachTargetRole"
            @created-and-attached="handleResponsibilityCreatedAndAttached"
        />
    </div>
</template>

<script setup>
import { defineProps, ref, onMounted, computed, watch, getCurrentInstance } from "vue";
import OrganizationRolesTable from "@components/OrganizationRolesTable/OrganizationRolesTable.vue";
import { LD_FLAG_IDEA_MDP_560 } from "@components/constants/launchDarklyFlagKeys";
import { convertRoleFiltersToApiFormat } from "@components/FilterRolesModal/roleFilterPayload";
import AttachedResponsibilityToRole from "@components/RolesCatalog/AttachedResponsibilityToRole.vue";
import ActivateRoleDialog from "@components/ActivateRoleDialog/ActivateRoleDialog.vue";
import ActivateResponsibilityConfirmationModal from "@components/ActivateResponsibilityConfirmationModal/ActivateResponsibilityConfirmationModal.vue";
import RetireRoleDialog from "@components/RetireRoleDialog/RetireRoleDialog.vue";
import ReactivateRoleDialog from "@components/ReactivateRoleDialog/ReactivateRoleDialog.vue";
import RenameRoleDialog from "@components/RenameRoleDialog/RenameRoleDialog.vue";
import DeleteRoleDialog from "@components/DeleteRoleDialog/DeleteRoleDialog.vue";
import ArchivePopUp from "@components/common/ArchivePopUp.vue";
import AttachedResponsibility from "@components/RolesCatalog/AttachedRole.vue";
import {
    getListOfRolesAttachedToResponsibility,
    getChatterList,
    detachAttachments,
    fetchAttachableRoles,
    createRole,
    updateActiveRole,
    updateRetiredRole,
    updateReActiveRole,
    deleteReadinessRole,
    deleteReadinessRoleWithResponsibilities,
    updateArchiveRole,
    updateUnArchiveRole,
    fetchAttachableResponsibilities,
    attachResponsibilityToRole,
} from "@/api";
import { ACTIVE_ATTACHED_ROLES_FILTERS, ALL_ATTACHED_ROLES_FILTERS } from "@components/utils/activeAttachedRolesCount";
import { fetchRoleActivationCount } from "@/utils/activationEmployeeCount";

const instance = getCurrentInstance();
const $loader = instance?.proxy?.$loader;
const $notifications = instance?.proxy?.$notifications;

const props = defineProps({
    responsibilityId: {
        required: false,
        default: null,
        validator: (v) =>
            v === null ||
            v === undefined ||
            typeof v === "number" ||
            typeof v === "string",
    },
    responsibilityName: {
        type: String,
        default: '',
    },
    /** When the responsibility is archived, attached-role mutations are blocked in the table UI. */
    responsibilityArchived: {
        type: Boolean,
        default: false,
    },
    /**
     * When archived, also show EXTooltip on disabled attach/detach controls.
     */
    showTooltipWhenAttachedRolesBlocked: {
        type: Boolean,
        default: false,
    },
    /** When true (e.g. Positions Tree View), role drilldown stays in same panel */
    drilldownInPanel: {
        type: Boolean,
        default: false,
    },
});

const rolesData = ref([]);
const paginationModel = ref({
    page: 1,
    total: 0,
    perPage: 10,
});

const searchQuery = ref("");
const organizationRolesTableRef = ref(null);
const appliedFilters = ref([]);
const accountId = ref(null);
const isLoading = ref(false);
// ER-10594 / MDP-560 — gate the attached-roles table skeleton behind the consistent
// skeleton-loading rollout flag so the legacy empty-state flash is preserved when off.
const skeletonLoadingEnabled = ref(false);
const skeletonLoading = computed(() => isLoading.value && skeletonLoadingEnabled.value);
const emit = defineEmits([
    'drilldown',
    'responsibility-data-updated',
    'attached-roles-search-sync',
    'attached-roles-count-updated',
]);

// Chatter related state
const chatterData = ref([]);
const totalChatterData = ref(0);
const loadedChatterPages = ref({});
const currentChatterRoleId = ref(null);

// Attach role state
const showAttachRoleDialog = ref(false);
const attachRoleError = ref("");
const attachableRoles = ref([]);
const hasMoreRolesData = ref(false);
const isLoadingMoreRoles = ref(false);
const isAssigningRole = ref(false);
const selectedRoleId = ref(null);
const rolesPage = ref(1);
const rolesSearchTerm = ref("");

// Role action dialog state
const showActivateRoleDialog = ref(false);
const showActivateResponsibility = ref(false);
const skipActivateButtonLoader = ref(false);
const activateAttachedButtonLoader = ref(false);
const showRetireRoleDialog = ref(false);
const showReActivateRoleDialog = ref(false);
const activationEmployeeCount = ref(0);
const showRenameRoleDialog = ref(false);
const showDeleteRoleDialog = ref(false);
const showArchiveDialog = ref(false);
const archiveAction = ref(null);
const currentRole = ref(null);

// Attach responsibility state
const showAttachResponsibilityDialog = ref(false);
const attachableResponsibilities = ref([]);
const hasMoreResponsibilitiesData = ref(false);
const isLoadingMoreResponsibilities = ref(false);
const isAssigningResponsibility = ref(false);
const selectedResponsibilityId = ref(null);
const currentRoleId = ref(null);
const responsibilitiesPage = ref(1);
const responsibilitiesSearchTerm = ref('');
const openDefaultAttachResponsibilityDialog = ref(false);

const attachTargetRole = computed(() => {
    const role = currentRole.value;
    const id = currentRoleId.value;
    const name = String(role?.role_name || role?.data?.name || '').trim();
    const code = String(role?.role_code || role?.data?.code || '').trim().replace(/^#+/, '').split(/\s+/)[0];
    return id && name && code ? { type: 'role', id, code, name } : null;
});

const responsibilityDataObject = computed(() => ({
    id: props.responsibilityId,
    name: props.responsibilityName,
}));

const detachContextObject = computed(() => ({
    id: props.responsibilityId,
    type: "responsibility",
    code: false,
    codeValue: "",
    name: props.responsibilityName,
}));

// Create a computed for pagination to ensure reactivity
const paginationComputed = computed(() => ({
    page: paginationModel.value.page,
    total: paginationModel.value.total,
    perPage: paginationModel.value.perPage,
}));

const attachedRoleNames = computed(() => rolesData.value.map(item => item.role_name));
const renameRoleModel = computed(() => {
    const role = currentRole.value || {};
    return {
        id: role.id || role.data?.id || null,
        name: role.name || role.role_name || role.data?.name || "",
        context: role.context || role.data?.context || "",
        status: role.status || role.data?.status || "",
        archived: role.archived ?? role.data?.archived ?? false,
    };
});
const draftResponsibilityCount = computed(() => currentRole.value?.info?.draft_responsibilities_count ?? 0);
const showAttachResponsibilitiesPicker = computed(() => {
    return instance?.proxy?.$store?.getters?.["featureFlags/ideaMDP389"];
});

function parseFilters(filters) {
    return convertRoleFiltersToApiFormat(filters || []);
}

async function fetchActiveAttachedRolesCount() {
    if (!accountId.value || !props.responsibilityId) {
        return 0;
    }

    try {
        const response = await getListOfRolesAttachedToResponsibility(
            accountId.value,
            1,
            0,
            "",
            props.responsibilityId,
            ACTIVE_ATTACHED_ROLES_FILTERS
        );
        const count = Number(response?.data?.count);
        return Number.isNaN(count) ? 0 : count;
    } catch (error) {
        console.warn("Could not fetch active attached roles count:", error?.message || error);
        return 0;
    }
}

async function emitActiveAttachedRolesCount(roleIds = []) {
    const count = await fetchActiveAttachedRolesCount();
    emit("attached-roles-count-updated", {
        responsibilityId: props.responsibilityId,
        count,
        roleIds,
    });
}

async function fetchRoles() {
    if (!accountId.value || !props.responsibilityId) {
        return;
    }
    
    // ER-10594 / MDP-560 — while the flag is on the skeleton is the loading affordance for
    // the table area, so the app-level overlay loader is suppressed instead of doubling up.
    const skeletonReplacesLoader = skeletonLoadingEnabled.value;
    try {
        isLoading.value = true;
        if (!skeletonReplacesLoader) {
            $loader?.show();
        }
        
        const offset = (paginationModel.value.page - 1) * paginationModel.value.perPage;
        const parsedFilters = parseFilters(appliedFilters.value);
        const filters =
            parsedFilters.status === undefined && parsedFilters.archived === undefined
                ? { ...parsedFilters, ...ALL_ATTACHED_ROLES_FILTERS }
                : parsedFilters;
        
        const response = await getListOfRolesAttachedToResponsibility(
            accountId.value,
            paginationModel.value.perPage,
            offset,
            searchQuery.value,
            props.responsibilityId,
            filters
        );
        
        if (response?.data) {
            rolesData.value = response.data.results.map((el) => ({
                id: el.id ?? el.role_id ?? el.role?.id ?? null,
                role_code: el.code != null && el.code !== "" ? String(el.code).replace(/^#+/, "") : "",
                role_name: el.name || "",
                status: el.status || "inactive",
                attached_position_count: el.position_attachment_count || 0,
                attached_res_count: el.responsibility_attachment_count || 0,
                active_attached_position_count: el.active_attached_position_count || 0,
                inactive_attached_position_count: el.inactive_attached_position_count || 0,
                active_attached_responsibilities_count: el.active_attached_responsibilities_count || 0,
                inactive_attached_responsibilities_count: el.inactive_attached_responsibilities_count || 0,
                archived: el.archived || false,
                draft_responsibilities_count: el.draft_responsibilities_count || 0,
            }));
            paginationModel.value.total = response.data.count || 0;
            await emitActiveAttachedRolesCount(rolesData.value.map((r) => String(r.id)));
        } else {
            rolesData.value = [];
            paginationModel.value.total = 0;
            await emitActiveAttachedRolesCount([]);
        }
    } catch (error) {
        console.error('Error fetching roles:', error);
        rolesData.value = [];
        paginationModel.value.total = 0;
        await emitActiveAttachedRolesCount([]);
    } finally {
        isLoading.value = false;
        if (!skeletonReplacesLoader) {
            $loader?.hide();
        }
    }
}

function getAttachedRolesListSearch() {
    const fromTable = organizationRolesTableRef.value?.searchQuery;
    if (typeof fromTable === "string") return fromTable;
    return searchQuery.value ?? "";
}

function handleSearchChange(searchText) {
    searchQuery.value = searchText;
    emit('attached-roles-search-sync', searchText ?? '');
    paginationModel.value.page = 1;
    fetchRoles();
}

function handleFilterChange(filters) {
    appliedFilters.value = filters;
    paginationModel.value.page = 1;
    fetchRoles();
}

function handlePageChange(pageData) {
    paginationModel.value.page = pageData.page;
    // perPage is maintained in paginationModel, only update if provided
    if (pageData.perPage) {
        paginationModel.value.perPage = pageData.perPage;
    }
    fetchRoles();
}

async function handleRowOperation(operation) {
    if (props.responsibilityArchived) return;
    const action = operation.item?.value;
    currentRole.value = operation.data;

    switch (action) {
        case 'activate_role':
            await openActivationConfirmation('activate');
            break;
        case 'retired_role':
            showRetireRoleDialog.value = true;
            break;
        case 're_activate_role':
            await openActivationConfirmation('reactivate');
            break;
        case 'attach_responsibility_role':
            currentRoleId.value = currentRole.value?.id || currentRole.value?.data?.id;
            openDefaultAttachResponsibilityDialog.value = true;
            showAttachResponsibilityDialog.value = true;
            responsibilitiesPage.value = 1;
            responsibilitiesSearchTerm.value = '';
            await fetchAttachableResponsibilitiesList();
            break;
        case 'rename_role':
            showRenameRoleDialog.value = true;
            break;
        case 'delete_role':
            showDeleteRoleDialog.value = true;
            break;
        case 'archive_action':
            archiveAction.value = 'archive';
            showArchiveDialog.value = true;
            break;
        case 'unarchive_action':
            archiveAction.value = 'unarchive';
            showArchiveDialog.value = true;
            break;
        case 'attach_role_positions_bulk':
            await reloadCurrentPage();
            break;
        default:
            await reloadCurrentPage();
            emit('responsibility-data-updated');
    }
}

function handleRoleDrillDown(roleData) {
    if (!roleData?.id) return;

    if (props.drilldownInPanel) {
        emit('drilldown', {
            roleId: roleData.id,
            tab: 'attached_responsibilities',
            breadcrumbs: roleData.breadcrumbs,
            roleName: roleData.role_name ?? roleData.name,
        });
        return;
    }

    const route = instance?.proxy?.$route;
    const router = instance?.proxy?.$router;
    if (!router) return;

    const positionId = route?.params?.positionId || route?.query?.positionId;
    if (positionId) {
        router.push({
            name: 'RoleDetailFromPosition',
            params: {
                positionId,
                roleId: roleData.id,
            },
            query: {
                positionName: route?.query?.positionName,
                roleTab: 'attached_responsibilities',
            },
        }).catch(() => {});
        return;
    }

    router.push({
        name: 'RoleDetailChild',
        params: { roleId: roleData.id },
        query: { roleTab: 'attached_responsibilities' },
    }).catch(() => {});
}

async function handleOpenChatterDrawer(data) {
    if (!data || !data.id) return;
    
    const roleId = data.id;
    
    // If switching to a different role, reset the chatter data
    if (currentChatterRoleId.value !== roleId) {
        chatterData.value = [];
        totalChatterData.value = 0;
        loadedChatterPages.value = {};
        currentChatterRoleId.value = roleId;
    }
    
    await fetchChatterData(roleId);
}

async function fetchChatterData(roleId) {
    if (!roleId || !accountId.value) return;
    
    const currentLength = chatterData.value.length;
    const page = Math.floor(currentLength / 10) + 1;
    
    // Initialize page tracking for this role if needed
    if (!loadedChatterPages.value[roleId]) {
        loadedChatterPages.value[roleId] = new Set();
    }
    
    // Don't reload already loaded pages
    if (loadedChatterPages.value[roleId].has(page)) {
        return;
    }
    
    try {
        const { data } = await getChatterList(accountId.value, roleId, "role", page);
        loadedChatterPages.value[roleId].add(page);
        
        if (data && data.count) {
            totalChatterData.value = data.count;
        }
        
        if (data && data.results) {
            const chatterItems = data.results.map((el) => {
                const date = new Date(el.recorded_on);
                const milliseconds = date.getTime();
                const millisecondsString = milliseconds.toString();
                const nameParts = el.recorded_by_name.split(" ");
                
                const newEl = {
                    id: el.id,
                    IsExIQtiveBot: false,
                    message: el.note,
                    created_on: millisecondsString,
                    IsHistory: el.is_history,
                };
                
                if (nameParts.length === 2) {
                    newEl.avatar = {
                        picture: "",
                        firstName: nameParts[0],
                        lastName: nameParts[1],
                    };
                } else {
                    newEl.avatar = {
                        picture: "",
                        firstName: el.recorded_by_name,
                        lastName: "",
                    };
                }
                
                return newEl;
            });
            chatterData.value.push(...chatterItems);
        }
    } catch (error) {
        console.error('Error fetching chatter data:', error);
    }
}

async function reloadCurrentPage() {
    await fetchRoles();
}

function handleOpenAttachRoleDialog() {
    if (props.responsibilityArchived) return;
    showAttachRoleDialog.value = true;
    rolesPage.value = 1;
    rolesSearchTerm.value = '';
    fetchAttachableRolesList();
}

async function fetchAttachableRolesList(isLoadMore = false) {
    if (!props.responsibilityId || !accountId.value) return;

    const limit = 10;
    const offset = (rolesPage.value - 1) * limit;
    const name = rolesSearchTerm.value || '';

    try {
        if (!isLoadMore) {
            attachableRoles.value = [];
        }
        isLoadingMoreRoles.value = true;

        const response = await fetchAttachableRoles(
            accountId.value,
            props.responsibilityId,
            limit,
            offset,
            name
        );

        const newItems = (response?.data?.results || []).map((item) => {
            const code = item.code || item.role_code || item.code_str?.match(/\d+/)?.[0] || '';
            return {
                ...item,
                id: item.id,
                role_code: code,
                code: code,
                name: item.name,
                status: item.status,
                attached: item?.attached_to_responsibility,
                disabled: item?.attached_to_responsibility,
            };
        });

        attachableRoles.value = isLoadMore
            ? [...attachableRoles.value, ...newItems]
            : newItems;

        const totalCount = response?.data?.count || 0;
        hasMoreRolesData.value = (rolesPage.value * limit) < totalCount;
    } catch (error) {
        console.error("Error fetching attachable roles:", error);
    } finally {
        isLoadingMoreRoles.value = false;
    }
}

function handleRoleSelected(role) {
    if (role && Object.prototype.hasOwnProperty.call(role, "id")) {
        selectedRoleId.value = role.id;
    }
}

async function handleAddNewRole(item) {
    try {
        $loader?.show();
        const payload = {
            name: item.name,
            account: accountId.value,
        };

        const { data } = await createRole(accountId.value, payload);
        selectedRoleId.value = data.id;
        $loader?.hide();

        await attachSelectedRoleToResponsibility();
    } catch (error) {
        console.error("Error creating role:", error);
        $loader?.hide();
    }
}

async function attachSelectedRoleToResponsibility() {
    if (!selectedRoleId.value || !props.responsibilityId) return;

    try {
        isAssigningRole.value = true;
        $loader?.setAppLoading(true);

        await attachResponsibilityToRole(accountId.value, selectedRoleId.value, {
            attachment_id: props.responsibilityId,
        });

        showAttachRoleDialog.value = false;
        selectedRoleId.value = null;
        rolesPage.value = 1;
        rolesSearchTerm.value = "";
        attachRoleError.value = "";

        await reloadCurrentPage();
        emit('responsibility-data-updated');
    } catch (error) {
        console.error("Error attaching role:", error);
        attachRoleError.value = error.response?.data?.[0] || "An error occurred while attaching the role";
    } finally {
        isAssigningRole.value = false;
        $loader?.setAppLoading(false);
    }
}

function loadMoreRoles() {
    if (isLoadingMoreRoles.value || !hasMoreRolesData.value) {
        return;
    }
    rolesPage.value += 1;
    fetchAttachableRolesList(true);
}

function searchRoles(searchTerm) {
    rolesSearchTerm.value = searchTerm;
    rolesPage.value = 1;
    fetchAttachableRolesList(false);
}

function closeAttachRoleDialog() {
    showAttachRoleDialog.value = false;
    selectedRoleId.value = null;
    rolesPage.value = 1;
    rolesSearchTerm.value = '';
    attachRoleError.value = "";
}

// ER-11818 / Roles Rules 3.4 & 3.6 — preview who will be assigned before activating or
// reactivating a Role. N > 0 opens the confirmation dialog with the count; N = 0 proceeds
// directly; an undeterminable preview falls back to the pre-existing dialog.
async function openActivationConfirmation(kind) {
    const idRole = currentRole.value?.id || currentRole.value?.data?.id;
    if (!accountId.value) {
        accountId.value = JSON.parse(localStorage.getItem("currentAccountID"));
    }
    const count = await fetchRoleActivationCount(kind, accountId.value, idRole);
    activationEmployeeCount.value = count ?? 0;
    if (count === 0) {
        await (kind === 'activate' ? onActivateRoleConfirm() : reActivateRole());
        return;
    }
    if (kind === 'activate') showActivateRoleDialog.value = true;
    else showReActivateRoleDialog.value = true;
}

const onActivateRoleConfirm = () => {
    const draftCount = draftResponsibilityCount.value;
    showActivateRoleDialog.value = false;
    if (draftCount > 0) {
        showActivateResponsibility.value = true;
        return;
    }
    processRoleActivation(false);
};

async function processRoleActivation(activateResponsibilities = false) {
    try {
        $loader?.setAppLoading(true);
        if (activateResponsibilities) {
            activateAttachedButtonLoader.value = true;
        } else {
            skipActivateButtonLoader.value = true;
        }

        const idRole = currentRole.value?.id || currentRole.value?.data?.id;
        const payload = {
            account_id: accountId.value,
            role_id: idRole,
            ...(activateResponsibilities && { activate_responsibilities: true }),
        };
        await updateActiveRole(accountId.value, idRole, payload);
        showActivateResponsibility.value = false;
        currentRole.value = null;
        await reloadCurrentPage();
        emit('responsibility-data-updated');
    } catch (error) {
        console.error('Error activating role:', error);
        showActivateResponsibility.value = false;
    } finally {
        $loader?.setAppLoading(false);
        skipActivateButtonLoader.value = false;
        activateAttachedButtonLoader.value = false;
    }
}

function skipActivateResponsibility() {
    processRoleActivation(false);
}

function activateAttachedResponsibilities() {
    processRoleActivation(true);
}

async function retireRole() {
    try {
        $loader?.setAppLoading(true);
        const idRole = currentRole.value?.id || currentRole.value?.data?.id;
        const nameRole = currentRole.value?.data?.name;
        await updateRetiredRole(accountId.value, idRole, {
            name: nameRole,
            status: "retired",
            account: accountId.value,
        });
        showRetireRoleDialog.value = false;
        currentRole.value = null;
        await reloadCurrentPage();
        emit('responsibility-data-updated');
    } catch (error) {
        console.error('Error retiring role:', error);
    } finally {
        $loader?.setAppLoading(false);
    }
}

async function reActivateRole() {
    try {
        $loader?.setAppLoading(true);
        const idRole = currentRole.value?.id || currentRole.value?.data?.id;
        const nameRole = currentRole.value?.data?.name;
        await updateReActiveRole(accountId.value, idRole, {
            name: nameRole,
            status: "inactive",
            account: accountId.value,
        });
        showReActivateRoleDialog.value = false;
        currentRole.value = null;
        await reloadCurrentPage();
        emit('responsibility-data-updated');
    } catch (error) {
        console.error('Error re-activating role:', error);
    } finally {
        $loader?.setAppLoading(false);
    }
}

async function onRoleRenamed() {
    try {
        $loader?.setAppLoading(true);
        showRenameRoleDialog.value = false;
        currentRole.value = null;
        await reloadCurrentPage();
        emit('responsibility-data-updated');
    } catch (error) {
        console.error('Error refreshing roles after rename:', error);
    } finally {
        $loader?.setAppLoading(false);
    }
}

async function deleteRole(deleteDraftResponsibilities = false) {
    try {
        $loader?.setAppLoading(true);
        const idRole = currentRole.value?.id || currentRole.value?.data?.id;
        const payload = {
            delete_draft_responsibilities: deleteDraftResponsibilities,
        };

        if (deleteDraftResponsibilities) {
            await deleteReadinessRoleWithResponsibilities(accountId.value, idRole, payload);
        } else {
            await deleteReadinessRole(accountId.value, idRole);
        }

        showDeleteRoleDialog.value = false;
        currentRole.value = null;
        await reloadCurrentPage();
        emit('responsibility-data-updated');
    } catch (error) {
        console.error('Error deleting role:', error);
    } finally {
        $loader?.setAppLoading(false);
    }
}

async function onArchiveRole() {
    try {
        $loader?.setAppLoading(true);
        const idRole = currentRole.value?.id || currentRole.value?.data?.id;

        if (archiveAction.value === 'archive') {
            await updateArchiveRole(accountId.value, 'role', idRole);
        } else {
            await updateUnArchiveRole(accountId.value, 'role', idRole);
        }

        showArchiveDialog.value = false;
        currentRole.value = null;
        await reloadCurrentPage();
        emit('responsibility-data-updated');
    } catch (error) {
        console.error('Error archiving/unarchiving role:', error);
    } finally {
        $loader?.setAppLoading(false);
    }
}

async function fetchAttachableResponsibilitiesList(isLoadMore = false) {
    if (!currentRoleId.value || !accountId.value) return;

    const limit = 10;
    const offset = (responsibilitiesPage.value - 1) * limit;
    const name = responsibilitiesSearchTerm.value || '';

    try {
        if (!isLoadMore) {
            attachableResponsibilities.value = [];
        }
        isLoadingMoreResponsibilities.value = true;

        const response = await fetchAttachableResponsibilities(
            accountId.value,
            currentRoleId.value,
            limit,
            offset,
            name
        );

        const newItems = (response?.data?.results || []).map((item) => ({
            id: item.id,
            code: item.code_str?.match(/\d+/)?.[0] || '',
            name: item.name,
            status: item.status,
            attached: item?.attached_to_role,
        }));

        attachableResponsibilities.value = isLoadMore
            ? [...attachableResponsibilities.value, ...newItems]
            : newItems;

        const totalCount = response?.data?.count || 0;
        hasMoreResponsibilitiesData.value = (responsibilitiesPage.value * limit) < totalCount;
    } catch (error) {
        console.error("Error fetching attachable responsibilities:", error);
    } finally {
        isLoadingMoreResponsibilities.value = false;
    }
}

function handleResponsibilitySelected(responsibility) {
    if (responsibility && Object.prototype.hasOwnProperty.call(responsibility, "id")) {
        selectedResponsibilityId.value = responsibility.id;
    }
}

async function handleAddNewResponsibility(item) {
    try {
        $loader?.show();
        const payload = {
            name: item.name,
            accountId: accountId.value,
        };
        const newResponsibility = await instance?.proxy?.$store?.dispatch(
            "readinessCatalogsStore/createResponsibility",
            payload
        );
        selectedResponsibilityId.value = newResponsibility.data.id;
        $loader?.hide();
        await handleAttachResponsibilityToRole();
    } catch (error) {
        console.error("Error creating responsibility:", error);
        $loader?.hide();
    }
}

async function handleAttachResponsibilityToRole() {
    if (!selectedResponsibilityId.value || !currentRoleId.value) return;

    try {
        isAssigningResponsibility.value = true;
        $loader?.setAppLoading(true);

        await attachResponsibilityToRole(accountId.value, currentRoleId.value, {
            attachment_id: selectedResponsibilityId.value,
        });

        showAttachResponsibilityDialog.value = false;
        selectedResponsibilityId.value = null;
        responsibilitiesPage.value = 1;
        responsibilitiesSearchTerm.value = '';
        await reloadCurrentPage();
        emit('responsibility-data-updated');
    } catch (error) {
        console.error("Error attaching responsibility:", error);
    } finally {
        isAssigningResponsibility.value = false;
        $loader?.setAppLoading(false);
    }
}

// AddAndAttachResponsibilityDialog already created and attached the Responsibility; just refresh.
async function handleResponsibilityCreatedAndAttached() {
    closeAttachResponsibilityDialog();
    await reloadCurrentPage();
    emit('responsibility-data-updated');
}

async function loadMoreResponsibilities() {
    if (isLoadingMoreResponsibilities.value || !hasMoreResponsibilitiesData.value) {
        return;
    }
    responsibilitiesPage.value += 1;
    await fetchAttachableResponsibilitiesList(true);
}

async function searchResponsibilities(searchTerm) {
    responsibilitiesSearchTerm.value = searchTerm;
    responsibilitiesPage.value = 1;
    await fetchAttachableResponsibilitiesList(false);
}

function closeAttachResponsibilityDialog() {
    showAttachResponsibilityDialog.value = false;
    openDefaultAttachResponsibilityDialog.value = false;
    selectedResponsibilityId.value = null;
    responsibilitiesPage.value = 1;
    responsibilitiesSearchTerm.value = '';
}

async function handleDetach(detachData) {
    if (props.responsibilityArchived) return;
    try {
        const targetObj = detachData.target;
        const detachObj = detachData.detachObj;
        
        if (!accountId.value) {
            accountId.value = JSON.parse(localStorage.getItem("currentAccountID"));
        }

        const roleId = targetObj?.objectId ?? targetObj?.id;
        const roleType = (targetObj?.objectType || targetObj?.type || "").toLowerCase();
        const detachType = (detachObj?.type || detachObj?.objectType || "").toLowerCase();

        if (!roleId || !detachObj?.id) {
            console.error("handleDetach: missing role or responsibility id", { targetObj, detachObj });
            $notifications?.error?.("Could not detach: missing role or responsibility.");
            return;
        }
        
        if (roleType === "role" && detachType === "responsibility") {
            if (String(detachObj.id) !== String(props.responsibilityId)) {
                $notifications?.error?.("Could not detach: invalid responsibility context.");
                return;
            }
            await detachAttachments(accountId.value, props.responsibilityId, {
                parent_id: roleId,
            });
        } else {
            console.warn("handleDetach: unsupported detach", { roleType, detachType });
            $notifications?.error?.("Could not detach: unsupported operation.");
            return;
        }
        
        await fetchRoles();
        emit('responsibility-data-updated');
        
        if (detachData.callBackFunc) {
            detachData.callBackFunc();
        }
    } catch (error) {
        console.error("Error detaching:", error);
        const detail = error?.response?.data?.detail;
        $notifications?.error?.(
            typeof detail === "string" ? detail : "Failed to detach. Please try again."
        );
    } finally {
        instance?.proxy?.$loader.setAppLoading(false);
    }
}

/** When detach context was missing on the table, detach using this tab's responsibility props. */
async function handleDetachInline({ target }) {
    if (!target?.objectId && !target?.id) {
        return;
    }
    const normalizedTarget = {
        ...target,
        objectId: target.objectId ?? target.id,
        objectType: target.objectType || "Role",
    };
    await handleDetach({
        target: normalizedTarget,
        detachObj: {
            id: props.responsibilityId,
            type: "responsibility",
            code: false,
            codeValue: "",
            name: props.responsibilityName || "",
        },
        callBackFunc: () => {
            organizationRolesTableRef.value?.handleInlineDetachComplete?.();
        },
    });
}

async function handleRequestMoreCheckpointsChatter(data) {
    if (currentChatterRoleId.value) {
        await fetchChatterData(currentChatterRoleId.value);
    }
}


// Expose method to parent to refresh roles data
function refreshRoles() {
    return fetchRoles();
}

// Expose the refresh method to parent components
defineExpose({
    refreshRoles,
    getAttachedRolesListSearch,
});

onMounted(async () => {
    try {
        accountId.value = JSON.parse(localStorage.getItem("currentAccountID"));
        // ER-10594 / MDP-560 — resolve the skeleton rollout gate before the initial
        // fetch so the skeleton (not the empty-state flash) shows while roles load.
        try {
            skeletonLoadingEnabled.value =
                (await instance?.proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_560, false)) === true;
        } catch {
            skeletonLoadingEnabled.value = false;
        }
        if (props.responsibilityId && accountId.value) {
            await fetchRoles();
        }
    } catch (error) {
        console.error('Error initializing component:', error);
    }
});

watch(
    () => props.responsibilityId,
    async (id) => {
        if (id && accountId.value) {
            paginationModel.value.page = 1;
            await fetchRoles();
        }
    }
);
</script>

<style scoped></style>